import React, { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { BookOpen } from 'lucide-react';

const API_BASE_URL = (() => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'https://api.neogtm.com';
  }
  return 'https://api.neogtm.com';
})();

export default function PlaybookIndicator({ type, id, tenantId, userId }) {
  const [isInPlaybook, setIsInPlaybook] = useState(false);
  const [playbookName, setPlaybookName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkPlaybookStatus = async () => {
      if (!id || !tenantId) {
        setLoading(false);
        return;
      }

      try {
        let endpoint;
        if (type === 'account') {
          const userParam = userId ? `&user_id=${encodeURIComponent(userId)}` : '';
          endpoint = `${API_BASE_URL}/api/playbooks/accounts/${id}/summary?tenant_id=${tenantId}${userParam}`;
        } else if (type === 'person') {
          const userParam = userId ? `&user_id=${encodeURIComponent(userId)}` : '';
          endpoint = `${API_BASE_URL}/api/personas/${id}/playbook-status?tenant_id=${tenantId}${userParam}`;
        }

        const response = await fetch(endpoint, { credentials: 'include' });
        
        if (response.ok) {
          const data = await response.json();
          
          if (type === 'account') {
            const hasActive = data.active_playbooks?.length > 0;
            setIsInPlaybook(hasActive);
            if (hasActive) {
              setPlaybookName(data.active_playbooks[0].playbook_name);
            }
          } else if (type === 'person') {
            setIsInPlaybook(data.is_enrolled || false);
            if (data.is_enrolled && data.playbook) {
              setPlaybookName(data.playbook.playbook_name);
            }
          }
        }
      } catch (error) {
        console.log('Playbook check skipped (API not available):', error.message);
      } finally {
        setLoading(false);
      }
    };

    checkPlaybookStatus();
  }, [type, id, tenantId, userId]);

  if (loading || !isInPlaybook) {
    return null;
  }

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center cursor-help">
            <Badge 
              variant="outline" 
              className="bg-purple-50 border-purple-200 text-purple-700 text-[10px] px-1.5 py-0 h-4 gap-1"
            >
              <BookOpen className="w-2.5 h-2.5" />
              <span>Playbook</span>
            </Badge>
          </span>
        </TooltipTrigger>
        <TooltipContent 
          side="top"
          className="bg-[#0A1A2F] text-white border-0 shadow-lg max-w-xs"
        >
          <p className="text-xs">
            <span className="font-semibold">Active in Playbook:</span>
            <br />
            {playbookName || 'Engaged in active playbook campaign'}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}